import React, { useState } from "react";
import AppLayout from "@/components/AppLayout";
import ClientLink from "@/components/ClientLink";
import { api } from "@/lib/api";
import { fmtDateIT } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, ScanSearch, AlertTriangle, Save } from "lucide-react";

const fmtMY = (v) => {
  if (!v) return "—";
  if (v.length === 7) { const [y, m] = v.split("-"); return `${m}/${y}`; }
  return fmtDateIT(v);
};

function Proposta({ p, checked, onToggle }) {
  const selectable = p.stato === "ok" || p.stato === "da_verificare";
  return (
    <div className={`bg-white border rounded-[3px] p-3 ${p.stato === "ok" ? "border-slate-200" : "border-amber-300"}`} data-testid={`dpr-prop-${p.client_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {selectable && <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`dpr-check-${p.client_id}`} />}
        <ClientLink clientId={p.client_id}>{p.cliente}</ClientLink>
        <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-slate-100 text-slate-600">CQS {p.estratti.rata}€ × {p.estratti.durata} mesi</span>
        {p.possibile_60_120 && <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-sky-100 text-sky-800 border border-sky-200">60→120 da verificare</span>}
        {p.stato === "durata_non_gestita" && <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-rose-100 text-rose-700">durata non gestita</span>}
        {p.stato === "da_verificare" && <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-amber-100 text-amber-800">da verificare</span>}
        <span className="ml-auto text-xs text-slate-600">
          {p.dpr && <>DPR teorica <b className="tabular-nums">{fmtMY(p.dpr)}</b> · finestra contatto <b className="tabular-nums">{fmtMY(p.finestra_contatto)}</b>{p.dpr_passata && <span className="text-emerald-700"> (già raggiunta)</span>}</>}
        </span>
      </div>
      <div className="mt-1.5 text-xs text-slate-600">
        <span className="text-slate-500">Fonte ({p.fonte.tipo}{p.fonte.when ? ` del ${fmtDateIT(p.fonte.when)}` : ", senza data"}):</span> "{p.fonte.testo}"
        <span className="text-slate-400"> · decorrenza {fmtMY(p.estratti.decorrenza)} (precisione: {p.estratti.precisione})</span>
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5">Finanziamento ritenuto attuale: {p.finanziamento_attuale_motivo}</div>
      {(p.flags || []).map((f, i) => (
        <div key={i} className="text-[11px] text-amber-700 mt-0.5 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {f}</div>
      ))}
    </div>
  );
}

export default function DprRecoveryPage() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState({});
  const [saving, setSaving] = useState(false);

  const scan = async () => {
    setBusy(true); setSel({});
    try { const r = await api.get("/dpr-recovery/scan"); setD(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore scansione"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    const items = (d?.proposte || []).filter((p) => sel[p.client_id] && p.dpr_iso)
      .map((p) => ({ client_id: p.client_id, dpr_iso: p.dpr_iso, precisione: p.estratti.precisione, flag6012: p.possibile_60_120 }));
    if (!items.length) { toast.error("Seleziona almeno una proposta"); return; }
    setSaving(true);
    try {
      const r = await api.post("/dpr-recovery/save", { items });
      toast.success(`${r.data.salvate} DPR salvate come "calcolata da note" (recall non toccato)`);
      const skipped = r.data.results.filter((x) => x.esito !== "salvata");
      if (skipped.length) toast.warning(`${skipped.length} non salvate: ${skipped.map((x) => x.esito).join(", ")}`);
      scan();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const nSel = Object.values(sel).filter(Boolean).length;

  return (
    <AppLayout title="Recupero DPR da note" subtitle="Calcolatori · parser deterministico, zero AI"
      actions={
        <div className="flex gap-2">
          <button onClick={scan} disabled={busy} data-testid="dpr-scan-btn"
            className="px-3 py-2 text-sm bg-[#0A192F] text-white rounded-[3px] inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />} Scansiona note e aggiornamenti
          </button>
          {d && (
            <button onClick={save} disabled={saving || nSel === 0} data-testid="dpr-save-btn"
              className="px-3 py-2 text-sm bg-emerald-700 text-white rounded-[3px] inline-flex items-center gap-2 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva {nSel} selezionate
            </button>
          )}
        </div>
      }>
      <div className="space-y-3 max-w-5xl" data-testid="dpr-recovery-page">
        <div className="text-xs bg-slate-50 border border-slate-200 rounded-[3px] p-2.5 text-slate-600">
          Cerca clienti con colonna DPR vuota e dati CQS nel testo (es. "CQS 330 X 120 DEC 06/2020" → DPR teorica 06/2024, finestra 02/2024).
          Calcolo con il calcolatore DPR esistente (stessa tabella). La rata è conservata come informazione ma non determina la soglia.
          Il salvataggio è solo manuale sui selezionati, etichettato "DPR calcolata da note", non tocca il recall e non sovrascrive DPR esistenti.
          La DPR teorica è una soglia di rinnovo da verificare, non il giorno garantito di erogazione.
        </div>
        {!d && !busy && <div className="py-10 text-center text-slate-400 text-sm">Premi "Scansiona" per iniziare (sola lettura)</div>}
        {busy && <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
        {d && (
          <>
            <div className="text-xs text-slate-500" data-testid="dpr-scan-summary">
              Scansionati {d.scansionati} clienti · {d.proposte.length} proposte · {d.conflitti.length} conflitti · {d.discrepanze.length} discrepanze con DPR esistente
            </div>
            {d.proposte.length > 0 && (
              <label className="text-xs text-slate-700 inline-flex items-center gap-1.5">
                <input type="checkbox" data-testid="dpr-select-all"
                  checked={d.proposte.filter((p) => p.stato === "ok").every((p) => sel[p.client_id]) && d.proposte.some((p) => p.stato === "ok")}
                  onChange={(e) => { const s = {}; if (e.target.checked) d.proposte.forEach((p) => { if (p.stato === "ok") s[p.client_id] = true; }); setSel(s); }} />
                Seleziona tutte le proposte senza avvisi
              </label>
            )}
            <div className="space-y-2">
              {d.proposte.map((p) => (
                <Proposta key={p.client_id} p={p} checked={!!sel[p.client_id]}
                  onToggle={() => setSel((s) => ({ ...s, [p.client_id]: !s[p.client_id] }))} />
              ))}
              {d.proposte.length === 0 && <div className="py-6 text-center text-slate-400 text-sm bg-white border border-slate-200 rounded-[3px]">Nessuna proposta: nessun cliente con DPR vuota e dati CQS leggibili nel testo</div>}
            </div>
            {d.conflitti.length > 0 && (
              <div className="space-y-2" data-testid="dpr-conflicts">
                <div className="text-sm font-semibold text-slate-800">Conflitti (nessuna scelta automatica)</div>
                {d.conflitti.map((c, i) => (
                  <div key={i} className="bg-amber-50 border border-amber-200 rounded-[3px] p-3 text-xs text-amber-900">
                    <ClientLink clientId={c.client_id}>{c.cliente}</ClientLink> — {c.motivo}
                    {(c.estrazioni || []).map((e, j) => (
                      <div key={j} className="mt-1 text-amber-800">"{e.fonte?.testo}"{e.decorrenza ? ` → decorrenza ${fmtMY(e.decorrenza)}, ${e.durata} mesi` : ""}</div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {d.discrepanze.length > 0 && (
              <div className="space-y-2" data-testid="dpr-discrepancies">
                <div className="text-sm font-semibold text-slate-800">Discrepanze con DPR esistenti (non sovrascritte)</div>
                {d.discrepanze.map((p, i) => (
                  <div key={i} className="bg-sky-50 border border-sky-200 rounded-[3px] p-3 text-xs text-sky-900">
                    <ClientLink clientId={p.client_id}>{p.cliente}</ClientLink> — {p.motivo}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
